import React, { useState, useRef, useEffect } from "react";
import { MixerHorizontalIcon } from "@radix-ui/react-icons";
import { Button } from "../ui/Button";

interface ColumnSelectorProps {
  columns: string[];
  visibleColumns: Record<string, boolean>;
  onVisibilityChange: (columnId: string, isVisible: boolean) => void;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns,
  onVisibilityChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth;

        if (isMobilePortrait) {
          setDropdownPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right - 70,
          });
        } else {
          // For desktop/landscape: position below button, aligned to right
          setDropdownPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
          });
        }
      }
    };

    if (isOpen) {
      updatePosition();
      // Update position on scroll
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate visible count: if column is not in visibleColumns object, it's visible by default
  const visibleCount = columns.filter((col) => visibleColumns[col] !== false).length;

  // Check if all columns are selected
  const allSelected = visibleCount === columns.length;

  const handleToggleAll = () => {
    const newVisibility = !allSelected;
    columns.forEach((column) => {
      onVisibilityChange(column, newVisibility);
    });
  };

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center space-x-2"
      >
        <MixerHorizontalIcon className="h-4 w-4" />
        <span>Columns ({visibleCount})</span>
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-72 md:w-64 bg-white  shadow-2xl border border-border-gray z-[100] max-h-[80vh] md:max-h-96 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <label className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
                className="h-4 w-4 border-border-gray rounded cursor-pointer"
                style={{ accentColor: "#699CBB" }}
              />
              <span className="ml-3 text-sm font-semibold text-gray-800">Select All</span>
            </label>
          </div>
          <div className="p-2">
            {columns.map((column) => (
              <label
                key={column}
                className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={visibleColumns[column] !== false}
                  onChange={(e) => onVisibilityChange(column, e.target.checked)}
                  className="h-4 w-4 border-border-gray rounded cursor-pointer"
                  style={{ accentColor: "#699CBB" }}
                />
                <span className="ml-3 text-sm text-gray-700">{column}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
